import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Editor,
  EditorProvider,
  pathPointsInterpolator,
  getCompositionProps,
} from '@/lib/kinema';
import { KinemaShell } from '@/modules/kinema/shell/KinemaShell';
import { AssetKitProvider } from '@/modules/kinema/assetKit';
import { MediaOwnerProvider } from '@/modules/kinema/mediaOwnerContext';
import {
  EditorActionsProvider,
  type EditorActions,
} from '@/modules/kinema/shell/editorActionsContext';
import {
  getLayeredMedia,
  updateLayeredMedia,
  updateLayeredMediaPreview,
  createLayeredMediaRender,
  spawnLayeredMediaRender,
} from '@/modules/kinema/layeredMediaService';
import { MediaService } from '@services/media/MediaService';
import { OrganizationService } from '@services/organizations/OrganizationService';
import { appStore } from '@store/appStore';
import { loadFontsFromState } from '@/modules/kinema/fonts';
import { brandToAssetKit } from './assetKitAdapters';
import { useBrandColors } from '@/modules/adEditor/useBrandColors';
import { BRAND_IDENTITY_V2, useFeatureFlag } from '@/shared/featureFlags/useFeatureFlag';
import { createPreviewCapture } from './previewCapture';
import { Paths } from '@/paths';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';
import type { Brand } from '@models/brand.model';

function createPlaygroundEditor(canvas: { width: number; height: number }) {
  const editor = new Editor({ canvas, middleware: [], devtools: true });
  editor.registerInterpolator('pathPoints', pathPointsInterpolator);
  return editor;
}

export default function StandaloneEditor() {
  const { layeredMediaId: idStr } = useParams<{ layeredMediaId: string }>();
  const navigate = useNavigate();
  const layeredMediaId = idStr ? parseInt(idStr, 10) : NaN;

  const [editor, setEditor] = useState<Editor | null>(null);
  const [fontsReady, setFontsReady] = useState(false);
  const [brand, setBrand] = useState<Brand | null>(null);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [rendering, setRendering] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [staticMode, setStaticMode] = useState(false);
  const [projectName, setProjectName] = useState<string | null>(null);

  useDocumentTitle(projectName ?? "Create Studio");

  const editorRef = useRef<Editor | null>(null);
  const mainLayerRef = useRef<any>(null);
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  const previewCapture = useRef(
    createPreviewCapture({
      minIntervalMs: 5000,
      uploadMedia: (file, name) => new MediaService().uploadMedia(file, name),
      updatePreview: updateLayeredMediaPreview,
    }),
  ).current;

  useEffect(() => {
    if (isNaN(layeredMediaId) || editor) return;
    getLayeredMedia(layeredMediaId)
      .then((lm) => {
        const data = lm.data;
        const root = data?.layers?.[data.rootLayerId];
        if (!root) {
          setLoadError('Project data is empty.');
          return;
        }
        const e = createPlaygroundEditor({ width: root.width, height: root.height });
        e.loadState(data);
        setStaticMode(lm.type === 'image');
        setProjectName((lm as { name?: string }).name ?? root.name ?? null);
        setEditor(e);
      })
      .catch(() => setLoadError('Failed to load project.'));
  }, [layeredMediaId, editor]);

  const brandOrgId = String(appStore.loggedInUser.organization.id);
  useEffect(() => {
    if (brandOrgId === '-1') return;
    new OrganizationService()
      .getOrganizationBrand(brandOrgId)
      .then(setBrand)
      .catch(() => {});
  }, []);
  const brandColors = useBrandColors(brandOrgId, brand);
  const brandIdentityV2 = useFeatureFlag(BRAND_IDENTITY_V2);

  useEffect(() => {
    if (!editor) return;
    loadFontsFromState(editor.getState()).then(() => setFontsReady(true));
  }, [editor]);

  const save = useCallback(async () => {
    const e = editorRef.current;
    if (!e || saving) return;
    setSaving(true);
    try {
      const data = e.toJSON();
      const name = e.getState().layers[e.getState().rootLayerId]?.name || 'Untitled';
      await updateLayeredMedia(layeredMediaId, { name, data });
      setLastSaved(new Date());

      const main = mainLayerRef.current;
      if (main) {
        const comp = getCompositionProps(e.getState());
        previewCapture(layeredMediaId, main, comp).catch(() => {});
      }
    } catch (err) {
      console.error('Failed to save:', err);
    } finally {
      setSaving(false);
    }
  }, [layeredMediaId, saving, previewCapture]);

  const render = useCallback(async () => {
    if (rendering) return;
    setRendering(true);
    try {
      await save();
      const e = editorRef.current;
      const main = mainLayerRef.current;
      if (staticMode) {
        if (!e || !main) return;
        const comp = getCompositionProps(e.getState());
        const blob = await new Promise<Blob | null>((resolve) => {
          try {
            main.toBlob({
              x: 0, y: 0,
              width: comp.width,
              height: comp.height,
              mimeType: 'image/png',
              quality: 1,
              pixelRatio: 1,
              callback: (b: Blob | null) => resolve(b),
            });
          } catch {
            resolve(null);
          }
        });
        if (!blob) throw new Error('Static render capture failed');
        const file = new File([blob], 'render.png', { type: 'image/png' });
        const uploaded = await new MediaService().uploadMedia(file, 'render.png', {
          width: comp.width,
          height: comp.height,
        });
        const mediaId = typeof uploaded.id === 'string' ? parseInt(uploaded.id, 10) : uploaded.id;
        await createLayeredMediaRender(layeredMediaId, mediaId);
      } else {
        await spawnLayeredMediaRender(layeredMediaId);
      }
    } catch (err) {
      console.error('render failed', err);
    } finally {
      setRendering(false);
    }
  }, [layeredMediaId, rendering, save, staticMode]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [save]);

  const actions: EditorActions = { saving, lastSaved, save, rendering, render, renderError: null, staticMode };
  const assetKit = useMemo(
    () => brandToAssetKit(brand, brandColors, { typography: brandIdentityV2 }),
    [brand, brandColors, brandIdentityV2],
  );

  if (loadError) {
    return <div style={{ padding: 24, color: '#ef4444' }}>{loadError}</div>;
  }
  if (!editor || !fontsReady) {
    return <div style={{ padding: 24, color: '#94a3b8' }}>Loading...</div>;
  }

  return (
    <AssetKitProvider value={assetKit}>
      <MediaOwnerProvider value={appStore.loggedInUser.id}>
        <EditorActionsProvider value={actions}>
          <EditorProvider editor={editor}>
            <KinemaShell
              onBack={() => navigate(`/${Paths.CREATE}`)}
              onMainLayerReady={(layer) => {
                mainLayerRef.current = layer;
              }}
            />
          </EditorProvider>
        </EditorActionsProvider>
      </MediaOwnerProvider>
    </AssetKitProvider>
  );
}
